import React, { useState } from 'react';
import { useResetPasswordConfirm, useResetPassword } from '../../api/users';
import { useLoginModalContext } from '../../context/LoginModalContext';
import { useUserContext } from '../../context/UserContext';
import { useNotifications } from '../../context/NotificationContext';
import * as Sentry from '@sentry/react';
import { X, Sun } from 'lucide-react';
import './LoginModal.css';

interface LoginModalResetPasswordCodeProps {
  onClose: () => void;
}

const LoginModalResetPasswordCode: React.FC<LoginModalResetPasswordCodeProps> = ({ 
  onClose
}) => {
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const resetPasswordConfirmMutation = useResetPasswordConfirm();
  const resetPasswordMutation = useResetPassword();
  const { email, setCurrentStep } = useLoginModalContext();
  const { login } = useUserContext();
  const { showError, showSuccess } = useNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (email.trim() && code.trim()) {
      try {
        const result = await resetPasswordConfirmMutation.mutateAsync({ 
          email: email.trim(), 
          code: code.trim()
        });
        
        login(result.user);
        setCurrentStep('new_password');
      } catch (error: any) {
        console.error('Reset password code verification failed:', error);
        
        if (error?.response?.status === 400) {
          setErrorMessage('Invalid or expired reset code. Please try again.');
        } else {
          Sentry.captureException(error);
          setErrorMessage('Failed to verify reset code. Please try again.');
        }
      }
    }
  };

  const handleResendCode = async () => {
    setErrorMessage('');
    
    if (email.trim()) {
      try {
        await resetPasswordMutation.mutateAsync({ 
          email: email.trim()
        });
        showSuccess('Reset code sent to your email address.');
      } catch (error: any) {
        console.error('Failed to resend reset code:', error);
        showError('Failed to resend reset code. Please try again.', error);
      }
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button 
            className="modal-close-btn" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>

          <h1 className="modal-title">Enter Reset Code</h1>
          
          <p className="modal-description">
            Enter the reset code that was sent to your email address ({email}).
          </p>
          
          {errorMessage && (
            <div className="message message--error">
              {errorMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="resetCode">Reset Code</label>
              <input
                type="text"
                id="resetCode"
                className="form-input"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder="Enter reset code"
                required
                disabled={resetPasswordConfirmMutation.isPending}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn--primary btn--login"
              disabled={resetPasswordConfirmMutation.isPending || !code.trim()}
            >
              {resetPasswordConfirmMutation.isPending ? 'Verifying...' : 'Verify Code'}
            </button>
          </form>

          <div className="button-group">
            <p className="login-link">
              <button 
                type="button" 
                className="login-link-text" 
                onClick={handleResendCode}
                disabled={resetPasswordConfirmMutation.isPending || resetPasswordMutation.isPending}
              >
                {resetPasswordMutation.isPending ? 'Sending...' : 'Resend Code'}
              </button>
            </p>
            <p className="login-link">
              Back to <button 
                type="button" 
                className="login-link-text" 
                onClick={() => setCurrentStep('email')}
              >
                Login
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginModalResetPasswordCode;
